/**
 * Bildirim presenter'i
 */

import { AppState } from '../../modules/state.js';
import { NotificationsService } from './notifications.service.js';
import type { NotificationRecipient, NotificationTemplate } from './notifications.types.js';

interface NotificationsPresenterDeps {
    closeModal: (modalId: string) => void;
    showToast: (message: string, type?: string) => void;
    onSelectionChanged: () => void;
}

function getSelectedRecipientType(): string {
    return (document.querySelector('input[name="recipient-type"]:checked') as HTMLInputElement | null)?.value || 'all';
}

function collectCustomRecipients(): NotificationRecipient[] {
    const recipients: NotificationRecipient[] = [];
    document.querySelectorAll('#apartment-checkboxes input:checked').forEach(input => {
        const apartmentNo = parseInt((input as HTMLInputElement).value, 10);
        const apartment = AppState.apartments.find(item => item.number === apartmentNo);
        if (apartment?.phone) {
            recipients.push({
                apartmentNo: apartment.number,
                residentName: apartment.residentName || 'Sakin',
                phoneNumber: apartment.phone,
                status: 'pending'
            });
        }
    });
    return recipients;
}

function getRecipientsForSelection(): NotificationRecipient[] {
    const selectedType = getSelectedRecipientType();
    if (selectedType === 'all') {
        return NotificationsService.getRecipients();
    }

    if (selectedType === 'unpaid') {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        return NotificationsService.getUnpaidDuesRecipients(currentYear, currentMonth);
    }

    return collectCustomRecipients();
}

function setNotificationSubmitState(isLoading: boolean): void {
    const submitButton = document.getElementById('send-notification-submit') as HTMLButtonElement | null;
    if (!submitButton) return;

    const buttonText = submitButton.querySelector('.btn-text') as HTMLElement | null;
    const buttonLoading = submitButton.querySelector('.btn-loading') as HTMLElement | null;

    if (buttonText) buttonText.classList.toggle('is-hidden', isLoading);
    if (buttonLoading) buttonLoading.classList.toggle('is-hidden', !isLoading);
    submitButton.disabled = isLoading;
}

export function updateNotificationPreviewPresenter(): void {
    const message = (document.getElementById('notification-message') as HTMLTextAreaElement | null)?.value || '';
    const previewEl = document.getElementById('notification-preview-text');
    if (previewEl) {
        const preview = message
            .replace(/{residentName}/g, 'Örnek Sakin')
            .replace(/{apartmentNo}/g, '1');
        previewEl.textContent = preview || 'Mesaj önizlemesi burada görünecek';
    }

    const countEl = document.getElementById('recipient-count');
    if (countEl) {
        countEl.textContent = String(getRecipientsForSelection().length);
    }
}

export function populateApartmentCheckboxesPresenter(deps: NotificationsPresenterDeps): void {
    const container = document.getElementById('apartment-checkboxes');
    if (!container) return;

    container.innerHTML = AppState.apartments.map(apartment => `
        <label class="checkbox-item ${!apartment.phone?.trim() ? 'disabled' : ''}">
            <input type="checkbox" value="${apartment.number}" ${!apartment.phone?.trim() ? 'disabled' : ''}>
            <span>Daire ${apartment.number} - ${apartment.residentName || 'Boş'} ${!apartment.phone?.trim() ? '(Tel. yok)' : ''}</span>
        </label>
    `).join('');

    container.querySelectorAll('input').forEach(input => {
        input.addEventListener('change', deps.onSelectionChanged);
    });
}

export function renderNotificationHistoryPresenter(): void {
    const list = document.getElementById('notifications-history');
    if (!list) return;

    const history = NotificationsService.getHistory();
    if (history.length === 0) {
        list.innerHTML = '<p class="empty-state">Henüz bildirim gönderilmemiş</p>';
        return;
    }

    list.innerHTML = history.map(item => `
        <div class="transaction-item notification-history-item">
            <div class="notification-history-main">
                <div class="notification-history-title">
                    ${item.templateType === 'due_reminder' ? '💰 Aidat Hatırlatma' :
            item.templateType === 'general_message' ? '📢 Genel Duyuru' :
                item.templateType === 'maintenance_notice' ? '🔧 Bakım Bildirimi' : '💬 Özel Mesaj'}
                </div>
                <div class="notification-history-meta">
                    ${new Date(item.sentAt).toLocaleString('tr-TR')} • ${item.recipientCount} Alıcı
                </div>
                <div class="notification-history-message">
                    ${item.message.substring(0, 50)}${item.message.length > 50 ? '...' : ''}
                </div>
            </div>
            <div class="status-badge ${item.failedCount === 0 ? 'status-paid' : 'status-unpaid'} notification-history-status">
                ${item.successCount} Başarılı / ${item.failedCount} Hata
            </div>
        </div>
    `).join('');

    const totalSentEl = document.getElementById('total-notifications-sent');
    if (totalSentEl) {
        totalSentEl.textContent = String(history.reduce((sum, item) => sum + item.successCount, 0));
    }
}

export function handleRecipientTypeChangePresenter(deps: NotificationsPresenterDeps): void {
    const selectedValue = getSelectedRecipientType();
    const customGroup = document.getElementById('custom-recipients-group');
    if (customGroup) {
        customGroup.classList.toggle('is-hidden', selectedValue !== 'custom');
    }

    if (selectedValue === 'custom') {
        populateApartmentCheckboxesPresenter(deps);
    }

    updateNotificationPreviewPresenter();
}

export async function submitNotificationFormPresenter(deps: NotificationsPresenterDeps): Promise<void> {
    const instanceInput = document.getElementById('greenapi-instance') as HTMLInputElement | null;
    const tokenInput = document.getElementById('greenapi-token') as HTMLInputElement | null;
    const messageInput = document.getElementById('notification-message') as HTMLTextAreaElement | null;
    const templateSelect = document.getElementById('notification-template') as HTMLSelectElement | null;

    const instanceId = instanceInput?.value?.trim() || AppState.settings.greenApiIdInstance;
    const apiToken = tokenInput?.value?.trim() || AppState.settings.greenApiToken;

    if (!instanceId || !apiToken) {
        deps.showToast('GREEN-API yapılandırması eksik. Lütfen Instance ID ve API Token girin.', 'error');
        document.querySelector('.greenapi-config')?.scrollIntoView({ behavior: 'smooth' });
        return;
    }

    const message = messageInput?.value || '';
    if (!message.trim()) {
        deps.showToast('Mesaj içeriği gerekli', 'error');
        return;
    }

    const recipients = getRecipientsForSelection();
    if (recipients.length === 0) {
        deps.showToast('Telefon numarası olan alıcı bulunamadı', 'error');
        return;
    }

    setNotificationSubmitState(true);

    try {
        const result = await NotificationsService.sendBulk(
            recipients,
            message,
            (templateSelect?.value || 'custom') as NotificationTemplate,
            { idInstance: instanceId, apiTokenInstance: apiToken }
        );

        if (result.successCount > 0) {
            (document.getElementById('notification-form') as HTMLFormElement | null)?.reset();
            deps.closeModal('notification-modal');
            if (AppState.currentSection === 'notifications') {
                renderNotificationHistoryPresenter();
            }
            updateNotificationPreviewPresenter();
        }
    } catch (error) {
        console.error('Bildirim gönderim hatası:', error);
        deps.showToast('Gönderim sırasında hata oluştu', 'error');
    } finally {
        setNotificationSubmitState(false);
    }
}
